import React from 'react';
import { Mail, Phone } from 'lucide-react';

/**
 * Inline email + phone fields embedded directly into paywalls (IQ + Personality).
 * Keeps the post-test contact capture to ONE click for the user:
 *   type → click "Unlock for ₹X" → Razorpay opens.
 *
 * Parent owns state + submit handling. Just passes value/onChange/error.
 *
 * Props:
 *   email, phone, onEmailChange, onPhoneChange, onEmailBlur, error
 *   tone: 'light' (for paywalls on hardcoded light gradients) | 'theme' (default)
 */
export default function InlineContactFields({
  email,
  phone,
  onEmailChange,
  onPhoneChange,
  onEmailBlur,
  error,
  tone = 'theme',
}) {
  // Paywalls like IQ use a fixed light gradient even in dark mode. For those
  // we need fixed dark ink on white inputs so the fields stay readable.
  const isLight = tone === 'light';
  const inputStyle = isLight
    ? { background: '#FFFFFFCC', color: '#1F2937', borderColor: '#D1D5DB' }
    : { background: 'var(--bg)', color: 'var(--text)', borderColor: 'var(--border)' };
  const iconColor = isLight ? '#6B7280' : 'var(--muted-fg)';
  const labelColor = isLight ? '#6B7280' : 'var(--muted-fg)';

  return (
    <div className="text-left max-w-sm mx-auto mb-4">
      {/* Email (required) */}
      <label className="block text-[11px] font-semibold mb-1 uppercase tracking-widest" style={{ color: labelColor }}>
        Email <span style={{ color: '#DC2626' }}>*</span>
      </label>
      <div className="relative mb-3">
        <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: iconColor }} />
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onBlur={onEmailBlur}
          placeholder="you@example.com"
          data-testid="inline-contact-email"
          className="w-full pl-10 pr-3 py-2.5 rounded-lg border outline-none text-sm"
          style={inputStyle}
        />
      </div>

      {/* Phone (optional) */}
      <label className="block text-[11px] font-semibold mb-1 uppercase tracking-widest" style={{ color: labelColor }}>
        Phone <span className="font-normal normal-case tracking-normal" style={{ opacity: 0.7 }}>(optional)</span>
      </label>
      <div className="relative mb-2">
        <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: iconColor }} />
        <input
          type="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="+91 98765 43210"
          data-testid="inline-contact-phone"
          className="w-full pl-10 pr-3 py-2.5 rounded-lg border outline-none text-sm"
          style={inputStyle}
        />
      </div>

      {error && (
        <p className="text-xs mt-1" style={{ color: '#DC2626' }} data-testid="inline-contact-error">{error}</p>
      )}
    </div>
  );
}
